import React, { useState, useEffect, useCallback } from 'react';
import { X, Maximize2, ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { useExpertChat } from '../../contexts/ExpertChatContext';
import { useExpertStream } from '../../hooks/useExpertStream';
import { useActiveSpeaker } from '../../hooks/useActiveSpeaker';
import { useAvatarState } from '../../hooks/useAvatarState';
import { useAuth } from '../../contexts/AuthContext';
import { expertApi } from '../../services/expertApi';
import ConversationList from './ConversationList';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import AvatarOverlay from '../avatar/AvatarOverlay';
import PersonaSelector from '../chat/PersonaSelector';
import { PermissionGate } from '../PermissionGate';

const ChatDrawer: React.FC = () => {
  const navigate = useNavigate();
  const { isDrawerOpen, closeDrawer, activeConversationId, setConversation, voiceModeEnabled, setVoiceModeEnabled, selectedPersonaSlug, setSelectedPersonaSlug } = useExpertChat();
  const { isAuthenticated } = useAuth();
  const activeSpeaker = useActiveSpeaker();

  // H1 fix: fetch banker preferences to read banker_voice_autoplay
  const { data: bankerPrefs } = useQuery({
    queryKey: ['banker-preferences'],
    queryFn: () => expertApi.getBankerPreferences(),
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  });

  const { messages, streamingMessage, isStreaming, error, sendMessage, loadConversation, clearMessages } = useExpertStream({
    tts: activeSpeaker,
    voiceMode: voiceModeEnabled,
    bankerAutoplay: bankerPrefs?.banker_voice_autoplay ?? false,
    bankerVoice: bankerPrefs?.banker_advisor_voice ?? undefined,
    analystVoice: bankerPrefs?.banker_analyst_voice ?? undefined,
    personaSlug: selectedPersonaSlug,
  });
  const [showConversations, setShowConversations] = useState(true);

  // Avatar state (Story 7.4)
  const { avatarState, isVisible, setIsVisible } = useAvatarState({
    isQueuePlaying: activeSpeaker.isQueuePlaying,
    isPlaying: activeSpeaker.isPlaying,
    isStreaming,
  });

  const avatarEnabled = bankerPrefs?.banker_avatar_enabled ?? false;
  const avatarPersona = selectedPersonaSlug;
  const avatarPersonaName = selectedPersonaSlug === 'banker'
    ? (bankerPrefs?.banker_advisor_name ?? 'Investment Banker')
    : selectedPersonaSlug === 'copilot'
    ? 'Market Copilot'
    : (bankerPrefs?.banker_analyst_name ?? 'Market Analyst');

  // H1 fix: track convergence from banker-notification events (AC3 badge)
  const [isConvergence, setIsConvergence] = useState(false);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ isConvergence?: boolean }>).detail;
      setIsConvergence(detail?.isConvergence ?? false);
    };
    window.addEventListener('banker-notification', handler);
    return () => window.removeEventListener('banker-notification', handler);
  }, []);

  // Stop TTS when drawer closes so audio doesn't keep playing in the background
  useEffect(() => {
    if (!isDrawerOpen) {
      activeSpeaker.stop();
    }
  }, [isDrawerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load conversation when activeConversationId changes
  useEffect(() => {
    if (activeConversationId) {
      loadConversation(activeConversationId);
      setShowConversations(false);
    }
  }, [activeConversationId, loadConversation]);

  const handleSend = useCallback((message: string) => {
    sendMessage(message, activeConversationId ?? undefined);
  }, [sendMessage, activeConversationId]);

  const handleNewChat = useCallback(() => {
    setConversation(null);
    clearMessages();
    setShowConversations(false);
  }, [setConversation, clearMessages]);

  const handleSelectConversation = useCallback((id: number) => {
    setConversation(id);
  }, [setConversation]);

  const handleBack = useCallback(() => {
    setShowConversations(true);
  }, []);

  const handleExpand = useCallback(() => {
    closeDrawer();
    navigate('/trading/expert');
  }, [closeDrawer, navigate]);

  const toggleVoiceMode = useCallback(() => {
    if (voiceModeEnabled) {
      activeSpeaker.stop();
      toast('Voice mode off');
    } else {
      toast('Voice mode on');
    }
    setVoiceModeEnabled(!voiceModeEnabled);
  }, [voiceModeEnabled, setVoiceModeEnabled, activeSpeaker]);

  if (!isDrawerOpen) return null;

  return (
    <>
      {/* Avatar overlay — 3D for feature:banker-avatar-3d users, 2D fallback (AC4) */}
      {avatarEnabled && (
        <PermissionGate
          permission="feature:banker-avatar-3d"
          fallback={
            <PermissionGate permission="feature:banker-avatar-2d">
              <AvatarOverlay
                variant="2d"
                personaName={avatarPersonaName}
                persona={avatarPersona}
                avatarState={avatarState}
                isVisible={isVisible}
                onClose={() => setIsVisible(false)}
                isConvergence={isConvergence}
              />
            </PermissionGate>
          }
        >
          <AvatarOverlay
            variant="3d"
            personaName={avatarPersonaName}
            persona={avatarPersona}
            avatarState={avatarState}
            isVisible={isVisible}
            onClose={() => setIsVisible(false)}
            audioRef={activeSpeaker.audioRef}
            isConvergence={isConvergence}
          />
        </PermissionGate>
      )}

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20"
        onClick={closeDrawer}
      />

      {/* Drawer panel */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-[400px] max-w-full bg-slate-900 border-l border-slate-700 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="flex items-center gap-2">
            {!showConversations && (
              <button
                onClick={handleBack}
                className="p-1 text-slate-400 hover:text-slate-200 transition-colors"
                title="Back to conversations"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-lg font-medium text-slate-100">Expert Chat</h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleVoiceMode}
              className={`p-1.5 transition-colors ${voiceModeEnabled ? 'text-teal-400 hover:text-teal-300' : 'text-slate-400 hover:text-slate-200'}`}
              title={voiceModeEnabled ? 'Voice mode on' : 'Voice mode off'}
            >
              {voiceModeEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button
              onClick={handleExpand}
              className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"
              title="Expand to full page"
            >
              <Maximize2 size={16} />
            </button>
            <button
              onClick={closeDrawer}
              className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        {showConversations ? (
          <ConversationList
            activeId={activeConversationId}
            onSelect={handleSelectConversation}
            onNewChat={handleNewChat}
          />
        ) : (
          <>
            <MessageList messages={messages} streamingMessage={streamingMessage} />
            {error && (
              <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/30">
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}
            <PersonaSelector
              selectedSlug={selectedPersonaSlug}
              onSelect={setSelectedPersonaSlug}
            />
            <ChatInput onSend={handleSend} disabled={isStreaming} />
          </>
        )}
      </div>
    </>
  );
};

export default ChatDrawer;
